import React from "react";
import { Box, Skeleton } from "@mui/material";
import { COLORS } from "../../shared/styles";
import { PREVIEW_NATURAL_HEIGHT, PREVIEW_NATURAL_WIDTH } from "./preview/usePreviewScale";

function FieldSkeleton({
  labelWidth = "30%",
  inputHeight = 40,
  mt = 0,
  mb = 0,
  labelMb = "8px",
}) {
  return (
    <Box sx={{ mt, mb }}>
      <Skeleton variant="text" width={labelWidth} height={14} sx={{ mb: labelMb }} />
      <Skeleton variant="rounded" height={inputHeight} sx={{ borderRadius: "8px" }} />
    </Box>
  );
}

function GroupSelectSkeleton() {
  return (
    <Box sx={{ mt: "32px" }}>
      <Skeleton variant="text" width={120} height={34} />
      <Skeleton variant="rounded" height={40} sx={{ mt: "16px", borderRadius: "8px" }} />
    </Box>
  );
}

function AppearanceTabSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box>
        <Skeleton variant="text" width="30%" height={14} sx={{ mb: "8px" }} />
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: "8px", flexShrink: 0 }} />
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: "8px" }} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={1} sx={{ bgcolor: COLORS.borderLight }} />
      <Box>
        <Skeleton variant="text" width="35%" height={14} sx={{ mb: "8px" }} />
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: "8px" }} />
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: "8px" }} />
        </Box>
      </Box>
      <FieldSkeleton labelWidth="35%" />
      <FieldSkeleton labelWidth="45%" inputHeight={80} />
    </Box>
  );
}

function BehaviourTabSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {[1, 2, 3].map((key) => (
        <Box
          key={key}
          sx={{
            width: "100%",
            minHeight: "80px",
            borderRadius: "8px",
            border: `1px solid ${COLORS.border}`,
            background: COLORS.white,
            p: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="45%" height={22} />
            <Skeleton variant="text" width="70%" height={18} sx={{ mt: "4px" }} />
          </Box>
          <Skeleton variant="rounded" width={35} height={22} sx={{ borderRadius: "11px", flexShrink: 0 }} />
        </Box>
      ))}
    </Box>
  );
}

function InstallTabSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width={180} height={34} />
      <Skeleton variant="text" width="85%" height={18} sx={{ mt: "4px" }} />
      <Skeleton variant="rounded" height={192} sx={{ mt: "16px", borderRadius: "8px" }} />
      <Box
        sx={{
          mt: "24px",
          borderRadius: "8px",
          border: `1px solid ${COLORS.border}`,
          background: COLORS.white,
          p: "16px",
        }}
      >
        {[1, 2, 3].map((key, index) => (
          <Box
            key={key}
            sx={{
              pb: index < 2 ? "16px" : 0,
              mb: index < 2 ? "16px" : 0,
              borderBottom: index < 2 ? `1px solid ${COLORS.borderLight}` : "none",
            }}
          >
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width="95%" height={18} sx={{ mt: "4px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function WidgetPreviewSkeleton() {
  return (
    <Box sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", pt: "32px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "16px", flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: "24px" }}>
          <Skeleton variant="text" width={32} height={18} />
          <Skeleton variant="text" width={52} height={18} />
          <Skeleton variant="text" width={24} height={18} />
        </Box>
        <Skeleton variant="text" width={64} height={18} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center" }}>
        <Skeleton
          variant="rounded"
          width={PREVIEW_NATURAL_WIDTH}
          height={PREVIEW_NATURAL_HEIGHT}
          sx={{ borderRadius: "8px", flexShrink: 0 }}
        />
      </Box>
    </Box>
  );
}

function ChatWidgetSettingsSkeleton({
  activeTab,
  showGroupSelect = false,
}) {
  const renderTabSkeleton = () => {
    if (activeTab === "behaviour") return <BehaviourTabSkeleton />;
    if (activeTab === "install") return <InstallTabSkeleton />;
    return <AppearanceTabSkeleton />;
  };

  return (
    <>
      {showGroupSelect ? <GroupSelectSkeleton /> : null}
      <Box sx={{ mt: "24px" }}>{renderTabSkeleton()}</Box>
      <Box sx={{ mt: "32px" }}>
        <Skeleton variant="rounded" width={184} height={40} sx={{ borderRadius: "8px" }} />
      </Box>
    </>
  );
}

export { ChatWidgetSettingsSkeleton, WidgetPreviewSkeleton };
export default ChatWidgetSettingsSkeleton;